import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { managerIntelAPI, playersAPI } from '../../utils/api';
import DynastyYoYSection from './DynastyYoYSection';
import DossierSection, { DossierRail } from './DossierSection';
import { ShellRail } from '../../shell/AppShell';
import { SleeperAvatar, PlayerHeadshot } from '../../shell/SleeperAvatar';

const AnimatedDots = () => {
  const [dots, setDots] = useState('.');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '.') return '..';
        if (prev === '..') return '...';
        return '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span>{dots}</span>;
};


/**
 * ManagerIntelProfile — the dossier. Shell-native rebuild of the legacy
 * profile (single responsive render, rail-driven league/filter context).
 * Data layer and async behavior (2s sync poll, separate Dynasty YoY job)
 * are unchanged from the legacy surface.
 */

const POSITION_LABELS = { QB: 'Quarterbacks', RB: 'Running Backs', WR: 'Wide Receivers', TE: 'Tight Ends' };

// Career-stage ramp — brand heat, light is young, dark is aging.
const EXP_RAMP = [
  { key: 'rookies', label: 'Rookies · 0-1 yrs', bg: '#FFE8D1', fg: '#1D1D1F' },
  { key: 'young', label: 'Young · 2-4 yrs', bg: '#FFBF85', fg: '#1D1D1F' },
  { key: 'prime', label: 'Prime · 5-7 yrs', bg: 'var(--ft-brand, #FF7A00)', fg: '#FFFFFF' },
  { key: 'aging', label: 'Aging · 8+ yrs', bg: '#1D1D1F', fg: '#FFFFFF' },
];

// Pulsing avatar of the manager being scouted — the waiting state.
// Falls back to the Fantasy Taco mark when no Sleeper avatar exists.
const DossierPulse = ({ avatarId, name, size = 64 }) => (
  <span
    style={{
      position: 'relative', display: 'inline-flex', borderRadius: '50%', flex: 'none',
      animation: 'ft-mi-ping 1.6s cubic-bezier(0.16, 1, 0.3, 1) infinite',
    }}
  >
    <span style={{ display: 'inline-flex', borderRadius: '50%', overflow: 'hidden', animation: 'ft-mi-bob 1.6s ease-in-out infinite' }}>
      {avatarId ? (
        <SleeperAvatar id={avatarId} name={name} size={size} />
      ) : (
        <img src="/logo-avatar.png" alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
      )}
    </span>
  </span>
);

const avatarCacheKey = (username) => `ft_mi_avatar_${username}`;

// Cycling narration under the pulsing avatar — what the scout is doing
// while the profile loads. Edit freely; shown in random order, so with
// this many lines a repeat is rare.
const LOADING_LINES = [
  'Pulling leagues…',
  'Analyzing trades…',
  'Reading rosters…',
  'Profiling tendencies…',
  'Checking the trophy case…',
  'Counting abandoned teams…',
  'Interviewing former league mates…',
  'Reviewing the tape…',
  'Digging through old trade offers…',
  'Measuring pick hoarding…',
  'Tracing draft capital…',
  'Auditing roster construction…',
  'Inspecting the taxi squad…',
  'Following the money…',
  'Cross-referencing alibis…',
  'Decoding trade patterns…',
  'Weighing the win-now moves…',
  'Sniffing out fire sales…',
  'Flagging suspicious activity…',
  'Building the profile…',
];

const LoadingNarration = () => {
  const [lines] = useState(() => {
    const arr = [...LOADING_LINES];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });
  const [lineIdx, setLineIdx] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setLineIdx((i) => (i + 1) % lines.length), 1400);
    return () => clearInterval(timer);
  }, [lines.length]);
  return (
    <div key={lineIdx} className="ft-fade ft-play" style={{ fontSize: 13, color: 'var(--ft-ink-3)', marginTop: 3 }}>
      {lines[lineIdx]}
    </div>
  );
};

// Shown only for long syncs (many leagues): the sync runs on the worker
// dyno server-side, independent of this tab, so reassure them they can
// leave and give them something to do while it crunches.
// Conservative per-league seed; once ≥5 are processed the live observed
// rate takes over with an EMA to smooth bursts without locking in low estimates.
const SYNC_SECONDS_PER_LEAGUE = 15;

const formatEtaLeft = (seconds) => {
  if (!seconds || seconds < 90) return 'a few minutes';
  const min = Math.round(seconds / 60);
  return `about ${min} minute${min === 1 ? '' : 's'}`;
};

const SyncWaitPanel = ({ leaguesTotal, username, startedAt, processed = 0 }) => {
  const handle = username ? `@${username}` : 'This manager';
  const etaRef = React.useRef(null);
  let rawRemaining;
  if (leaguesTotal <= processed) {
    rawRemaining = 0;
  } else if (startedAt && processed >= 5) {
    const elapsed = (Date.now() - startedAt) / 1000;
    rawRemaining = (elapsed / processed) * (leaguesTotal - processed);
  } else {
    rawRemaining = SYNC_SECONDS_PER_LEAGUE * (leaguesTotal - processed);
  }
  // EMA smoothing: allows the estimate to rise if early leagues were fast,
  // but dampens sudden jumps from burst completions.
  const remaining = etaRef.current !== null
    ? 0.6 * rawRemaining + 0.4 * etaRef.current
    : rawRemaining;
  etaRef.current = remaining;
  return (
    <div style={{ marginTop: 28, maxWidth: 384, marginLeft: 'auto', marginRight: 'auto' }}>
      <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ft-ink-2, #6E6E73)', margin: 0 }}>
        {handle} is in {leaguesTotal} leagues — {formatEtaLeft(remaining)}, and it's worth the wait. It keeps running in the background, so go cruise the rest of the site and check back. The read will be ready when you are.
      </p>
      <button
        className="ft-btn"
        style={{ marginTop: 18 }}
        onClick={() => { window.open('/simulate', '_blank', 'noopener'); }}
      >
        Simulate your season while you wait
      </button>
    </div>
  );
};

const Stat = ({ value, label, sub }) => (
  <div style={{ minWidth: 64 }}>
    <div style={{ fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', color: 'var(--ft-ink)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
      {value}
    </div>
    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ft-ink-3)', marginTop: 3 }}>
      {label}
    </div>
    {sub ? (
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ft-brand-ink)', marginTop: 2 }}>{sub}</div>
    ) : null}
  </div>
);

const SectionHead = ({ k, sub }) => (
  <div style={{ margin: '2px 0 18px' }}>
    <span className="ft-k">{k}</span>
    {sub ? <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 6, marginBottom: 0 }}>{sub}</p> : null}
  </div>
);

const MI_CSS = `
@keyframes ft-syncpulse { 0%, 100% { transform: scale(1); opacity: .4; } 50% { transform: scale(1.4); opacity: 1; } }
@keyframes ft-mi-ping { 0% { box-shadow: 0 0 0 0 rgba(255, 122, 0, .30); } 75% { box-shadow: 0 0 0 22px rgba(255, 122, 0, 0); } 100% { box-shadow: 0 0 0 0 rgba(255, 122, 0, 0); } }
@keyframes ft-mi-bob { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
.ft-mi-statband { display: flex; flex-wrap: wrap; gap: 18px 40px; padding: 14px 0 20px; border-bottom: 1px solid var(--ft-hairline); }
.ft-mi-statgroups { container-type: inline-size; margin-top: 22px; display: flex; flex-wrap: wrap; gap: 20px 0; }
.ft-mi-statgroup { padding-right: 48px; }
.ft-mi-statgroup + .ft-mi-statgroup { border-left: 1px solid var(--ft-hairline); padding-left: 48px; padding-right: 0; }
@container (max-width: 939px) {
  .ft-mi-statgroup { padding-right: 0; width: 100%; }
  .ft-mi-statgroup + .ft-mi-statgroup { border-left: none; padding-left: 0; border-top: 1px solid var(--ft-hairline); padding-top: 18px; }
}
.ft-mi-flowgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(172px, 1fr)); gap: 10px; margin-bottom: 24px; }
.ft-mi-trade { display: grid; grid-template-columns: 180px 1fr 30px 1fr; gap: 14px; padding: 14px 0; border-bottom: 1px solid var(--ft-hairline); align-items: flex-start; }
.ft-mi-trade .arrow { color: var(--ft-ink-3); font-size: 14px; text-align: center; padding-top: 17px; }
.ft-mi-rostergrid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 28px; }
.ft-mi-holdrow { display: flex; align-items: center; gap: 11px; padding: 8px 0; border-bottom: 1px solid var(--ft-hairline); }
.ft-mi-teambar { display: grid; width: calc(100% + 12px); grid-template-columns: 42px 1fr minmax(180px, auto); gap: 12px; align-items: center; padding: 8px 6px; margin: 0 -6px; border-radius: 10px; text-align: left; cursor: pointer; }
.ft-mi-teambar:hover { background: var(--ft-surface); }
@media (max-width: 760px) {
  .ft-mi-trade { grid-template-columns: 1fr; gap: 8px; }
  .ft-mi-trade .arrow { display: none; }
  .ft-mi-rostergrid { grid-template-columns: 1fr; }
  .ft-mi-teambar { grid-template-columns: 42px 1fr; }
  .ft-mi-teambar .meta { grid-column: 1 / -1; }
}
`;

const ManagerIntelProfile = () => {
  const { sleeperUsername } = useParams();
  const location = useLocation();

  const myRosterPlayerIds = location.state?.myRosterPlayerIds || [];
  const theirRosterPlayerIds = location.state?.theirRosterPlayerIds || [];
  const viewerUsername = location.state?.viewerUsername;
  const priorityLeagueId = location.state?.priorityLeagueId || null;
  const fromOrphanMarketplace = location.state?.fromOrphanMarketplace || false;

  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState(null);
  const [trophyCase, setTrophyCase] = useState(null);
  // Dossier payload, lifted so the fingerprint nav can live in the shell rail.
  const [dossierData, setDossierData] = useState(undefined);
  const [allTrades, setAllTrades] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [dynastyYoY, setDynastyYoY] = useState([]);
  const [dynastyYoYStatus, setDynastyYoYStatus] = useState('not_started');
  const [showDynastyYoYToast, setShowDynastyYoYToast] = useState(false);
  const [playerData, setPlayerData] = useState({});
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [error, setError] = useState(null);

  // Filter states
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [rosterFilter, setRosterFilter] = useState('all');
  const [playerSearch, setPlayerSearch] = useState('');

  // View toggle state
  // Default to dynastyYoY if available, otherwise trades
  const [activeView, setActiveView] = useState('trades'); // Will be set based on dynastyYoY availability
  const [selectedTeamLeagueId, setSelectedTeamLeagueId] = useState(null); // For team toggles in rosters/experience views - also used for roster view
  const [selectedNFLTeam, setSelectedNFLTeam] = useState(null); // For highlighting clicked team in team exposure

  // Ref to store polling interval for cleanup
  const pollIntervalRef = useRef(null);
  // When the in-progress sync started — drives the live ETA on the wait screen.
  const syncStartRef = useRef(null);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Clear selected NFL team when changing views
  useEffect(() => {
    setSelectedNFLTeam(null);
  }, [activeView]);

  useEffect(() => {
    loadProfile();
  }, [sleeperUsername]);

  // Honor ?view= and ?leagueId= deep-links from external pages (e.g. Scout
  // Canvas → "See roster"). Tracked via a ref so the dynastyYoY auto-switch
  // and other defaults don't clobber the explicit URL intent.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    const leagueIdParam = params.get('leagueId');
    if (view) {
      setActiveView(view);
      deepLinkAppliedRef.current = true;
    }
    if (leagueIdParam) {
      const asNum = Number(leagueIdParam);
      setSelectedTeamLeagueId(Number.isNaN(asNum) ? leagueIdParam : asNum);
      deepLinkAppliedRef.current = true;
    }
  }, [location.search]);

  // Set default view to dynastyYoY if available — but never override an
  // explicit ?view= deep-link.
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (dynastyYoY && dynastyYoY.length > 0 && activeView === 'trades') {
      setActiveView('dynastyYoY');
    }
  }, [dynastyYoY]);


  // Show toast when Dynasty YoY completes
  useEffect(() => {
    if (dynastyYoYStatus === 'complete' && dynastyYoY && dynastyYoY.length > 0) {
      setShowDynastyYoYToast(true);
      // Auto-hide after 4 seconds
      const timer = setTimeout(() => {
        setShowDynastyYoYToast(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [dynastyYoYStatus, dynastyYoY]);

  const formatLeagueSettings = (league) => {
    const s = league?.settings || {};
    const parts = [];
    if (s.numTeams) parts.push(`${s.numTeams}-team`);
    if (s.type) parts.push(s.type === 'dynasty' ? 'Dynasty' : 'Redraft');
    if (s.isSuperFlex) parts.push('Superflex');
    if (s.tePremium) parts.push(`TEP ${s.tePremium}`);
    if (typeof s.ppr === 'number' && s.ppr > 0) {
      parts.push(`${s.ppr === 1 ? '1.0' : s.ppr === 0.5 ? '0.5' : s.ppr} PPR`);
    } else if (s.ppr === 0) {
      parts.push('Standard');
    }
    return parts.join(' · ');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never synced';

    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const startPolling = () => {
    // Clear any existing polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Stamp the start so the wait screen can show a live ETA (best-effort if a
    // sync was already running when we started watching).
    if (!syncStartRef.current) syncStartRef.current = Date.now();

    // Poll for sync completion
    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await managerIntelAPI.getSyncStatus(sleeperUsername);
        setSyncStatus(statusResponse.data);

        const s = statusResponse.data.status;
        if (s === 'complete' || s === 'error' || s === 'stale') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          syncStartRef.current = null;
          await loadProfile(true);
        }
      } catch (err) {
        console.error('Failed to check sync status:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Safety timeout - stop polling after 10 minutes (large portfolios can take
    // several minutes to walk every league's prior-season chain).
    setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      syncStartRef.current = null;
      loadProfile(true);
    }, 600000);
  };

  const handleRefresh = async () => {
    try {
      await managerIntelAPI.triggerSync(sleeperUsername, priorityLeagueId, true);
      syncStartRef.current = Date.now();
      setSyncStatus({ status: 'in_progress', leaguesProcessed: 0, leaguesTotal: 0 });
      startPolling();
    } catch (err) {
      console.error('Failed to trigger sync:', err);
    }
  };

  const handleDynastyYoYUpdate = async () => {
    try {
      const response = await managerIntelAPI.getDynastyYoY(sleeperUsername);
      setDynastyYoY(response.data.dynastyYoY || []);
      // Update status to complete
      setDynastyYoYStatus('complete');
    } catch (err) {
      console.error('Failed to refresh Dynasty YoY:', err);
    }
  };

  const loadProfile = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setLoading(true);
      }
      setError(null);

      const profileResponse = await managerIntelAPI.getProfile(sleeperUsername, viewerUsername);
      setManager(profileResponse.data.manager);
      try {
        if (profileResponse.data.manager?.avatarUrl) {
          localStorage.setItem(avatarCacheKey(sleeperUsername), profileResponse.data.manager.avatarUrl);
        }
      } catch (e) { /* storage unavailable */ }
      setTrophyCase(profileResponse.data.trophyCase);
      setAllTrades(profileResponse.data.trades);
      setLeagues(profileResponse.data.leagues || []);
      setDynastyYoY(profileResponse.data.dynastyYoY || []);
      setLastRefreshedAt(profileResponse.data.lastRefreshedAt);

      const statusResponse = await managerIntelAPI.getSyncStatus(sleeperUsername);
      setSyncStatus(statusResponse.data);

      // Check Dynasty YoY status
      try {
        const dynastyYoYStatusResponse = await managerIntelAPI.getDynastyYoYStatus(sleeperUsername);
        setDynastyYoYStatus(dynastyYoYStatusResponse.data.status);
      } catch (err) {
        console.error('Failed to get Dynasty YoY status:', err);
      }

      // If sync is already in progress, start polling
      if (statusResponse.data.status === 'in_progress') {
        startPolling();
      }

      // If no trades and sync not started, trigger sync and start polling
      if (
        !fromOrphanMarketplace &&
        profileResponse.data.trades.length === 0 &&
        statusResponse.data.status === 'not_started'
      ) {
        await managerIntelAPI.triggerSync(sleeperUsername, priorityLeagueId);
        setSyncStatus({ status: 'in_progress', leaguesProcessed: 0, leaguesTotal: 0 });
        startPolling();
      }

      // Collect all player IDs from trades and rosters
      const playerIds = new Set();

      profileResponse.data.trades.forEach(trade => {
        Object.keys(trade.adds).forEach(id => playerIds.add(id));
        Object.keys(trade.drops).forEach(id => playerIds.add(id));
      });

      (profileResponse.data.leagues || []).forEach(league => {
        (league.currentRoster || []).forEach(id => playerIds.add(id));
      });

      if (playerIds.size > 0) {
        const playersResponse = await playersAPI.getBatch(Array.from(playerIds));
        setPlayerData(playersResponse.data.players || {});
      }

      if (!skipLoadingState) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to load manager profile:', err);
      setError(err.response?.data?.error || 'Failed to load manager profile');
      if (!skipLoadingState) {
        setLoading(false);
      }
    }
  };

  // Merge leagues: show the latest version of each league, but include all historical leagues too
  const mergedLeagues = useMemo(() => {
    const merged = [];
    const processedSleeperIds = new Set();

    // First, find all leagues that have a "next version" (rolled over)
    const hasNextVersion = new Set();
    leagues.forEach(league => {
      if (league.settings?.previousLeagueId) {
        hasNextVersion.add(league.settings.previousLeagueId);
      }
    });

    // Process leagues from newest to oldest
    const sortedLeagues = [...leagues].sort((a, b) => parseInt(b.season) - parseInt(a.season));

    sortedLeagues.forEach(league => {
      const sleeperLeagueId = league.sleeperLeagueId;

      // Skip if we've already processed this league chain
      if (processedSleeperIds.has(sleeperLeagueId)) {
        return;
      }

      // If this league has rolled over, skip it (we'll show the newer version)
      if (hasNextVersion.has(sleeperLeagueId)) {
        processedSleeperIds.add(sleeperLeagueId);
        return;
      }

      // This is either the latest version or a standalone league
      // Trace back through previous versions to build originalLeagueIds
      const originalLeagueIds = [league.id];
      let currentLeague = league;
      let avatarUrl = league.avatarUrl; // Start with current league's avatar
      let previousSeasonRecord = null;

      while (currentLeague.settings?.previousLeagueId) {
        const prevId = currentLeague.settings.previousLeagueId;
        const prevLeague = leagues.find(l => l.sleeperLeagueId === prevId);

        if (prevLeague) {
          originalLeagueIds.push(prevLeague.id);
          processedSleeperIds.add(prevLeague.sleeperLeagueId);

          // If current league doesn't have an avatar, use the previous league's avatar
          if (!avatarUrl && prevLeague.avatarUrl) {
            avatarUrl = prevLeague.avatarUrl;
          }

          // Keep the most recent predecessor's played record so a 0-0
          // rolled-over season still shows last season's result.
          const prevRecord = prevLeague.record;
          if (!previousSeasonRecord && prevRecord && ((prevRecord.wins || 0) + (prevRecord.losses || 0) + (prevRecord.ties || 0)) > 0) {
            previousSeasonRecord = prevRecord;
          }

          currentLeague = prevLeague;
        } else {
          break;
        }
      }

      processedSleeperIds.add(sleeperLeagueId);

      merged.push({
        ...league,
        originalLeagueIds,
        avatarUrl, // Use the avatar from the chain (preferring newer, falling back to older)
        previousSeasonRecord,
      });
    });

    return merged;
  }, [leagues]);

  // Deep-links may carry the 2025 league id, but merged leagues are keyed by
  // the 2026 successor's id (the 2025 id lives in originalLeagueIds).
  // Normalize once mergedLeagues is available so all selectedTeamLeagueId
  // comparisons resolve correctly.
  useEffect(() => {
    if (!selectedTeamLeagueId || selectedTeamLeagueId === 'all') return;
    if (!mergedLeagues || mergedLeagues.length === 0) return;
    const directHit = mergedLeagues.some(l => l.id === selectedTeamLeagueId);
    if (directHit) return;
    const owner = mergedLeagues.find(
      l => l.originalLeagueIds && l.originalLeagueIds.includes(selectedTeamLeagueId)
    );
    if (owner) {
      setSelectedTeamLeagueId(owner.id);
    }
  }, [mergedLeagues, selectedTeamLeagueId]);

  // Initialize selectedTeamLeagueId to 'all' when mergedLeagues changes
  useEffect(() => {
    if (mergedLeagues.length > 0 && !selectedTeamLeagueId) {
      setSelectedTeamLeagueId('all');
    }
  }, [mergedLeagues, selectedTeamLeagueId]);

  // Group trades by league - use managerIntelLeagueId instead
  const leagueGroups = useMemo(() => {
    const groups = {};
    allTrades.forEach(trade => {
      const leagueId = trade.managerIntelLeagueId || trade.league?.id;
      if (!groups[leagueId]) {
        groups[leagueId] = {
          league: trade.league,
          trades: [],
        };
      }
      groups[leagueId].trades.push(trade);
    });
    return groups;
  }, [allTrades]);

  // Get trade count for each merged league
  const leagueTradeCountsMap = useMemo(() => {
    const counts = {};
    mergedLeagues.forEach(league => {
      // Check both the main ID and any merged league IDs
      const leagueIds = league.originalLeagueIds || [league.id];
      let totalTrades = 0;
      leagueIds.forEach(id => {
        const group = leagueGroups[id];
        if (group) {
          totalTrades += group.trades.length;
        }
      });
      counts[league.id] = totalTrades;
    });
    return counts;
  }, [mergedLeagues, leagueGroups]);

  // Extract unique seasons
  const seasons = useMemo(() => {
    const uniqueSeasons = new Set(allTrades.map(t => t.league.season));
    return Array.from(uniqueSeasons).sort((a, b) => b - a);
  }, [allTrades]);

  // Extract positions from trades and rosters
  const positions = useMemo(() => {
    const posSet = new Set();
    let hasPicks = false;

    // Get positions from trades
    allTrades.forEach(trade => {
      [...Object.keys(trade.adds), ...Object.keys(trade.drops)].forEach(playerId => {
        const player = playerData[playerId];
        if (player?.position) posSet.add(player.position);
      });

      // Check if this trade has draft picks
      if (trade.draftPicksTraded && trade.draftPicksTraded.length > 0) {
        hasPicks = true;
      }
    });

    // Also get positions from rosters
    mergedLeagues.forEach(league => {
      if (league.currentRoster && Array.isArray(league.currentRoster)) {
        league.currentRoster.forEach(playerId => {
          const player = playerData[playerId];
          if (player?.position) posSet.add(player.position);
        });
      }
    });

    const posArray = Array.from(posSet).sort();
    if (hasPicks) {
      posArray.push('PICKS');
    }
    return posArray;
  }, [allTrades, playerData, mergedLeagues]);

  // Get current roster ownership across all leagues (using merged leagues to avoid duplicates)
  const rosterOwnership = useMemo(() => {
    const ownership = {};

    mergedLeagues.forEach(league => {
      if (league.currentRoster && Array.isArray(league.currentRoster)) {
        league.currentRoster.forEach(playerId => {
          if (!ownership[playerId]) {
            ownership[playerId] = [];
          }
          ownership[playerId].push({
            leagueName: league.name,
            leagueId: league.id,
          });
        });
      }
    });

    return ownership;
  }, [mergedLeagues]);

  // Group roster by position
  const rosterByPosition = useMemo(() => {
    const grouped = { QB: [], RB: [], WR: [], TE: [], Other: [] };

    Object.keys(rosterOwnership).forEach(playerId => {
      const player = playerData[playerId];
      const position = player?.position || 'Other';
      const posKey = ['QB', 'RB', 'WR', 'TE'].includes(position) ? position : 'Other';

      grouped[posKey].push({
        playerId,
        player,
        leagues: rosterOwnership[playerId],
      });
    });

    // Get total number of leagues for ownership percentage calculation
    const totalLeagues = mergedLeagues.length;

    // Sort by ownership percentage first (highest %), then by KTC value
    Object.keys(grouped).forEach(pos => {
      grouped[pos].sort((a, b) => {
        // Calculate ownership percentages
        const aOwnership = totalLeagues > 0 ? (a.leagues.length / totalLeagues) * 100 : 0;
        const bOwnership = totalLeagues > 0 ? (b.leagues.length / totalLeagues) * 100 : 0;

        // Primary sort: ownership percentage (descending)
        if (Math.abs(aOwnership - bOwnership) > 0.01) {
          return bOwnership - aOwnership;
        }

        // Secondary sort: KTC value (descending)
        const aKtc = a.player?.ktc_value_sf || 0;
        const bKtc = b.player?.ktc_value_sf || 0;
        return bKtc - aKtc;
      });
    });

    return grouped;
  }, [rosterOwnership, playerData, mergedLeagues]);

  // Calculate average roster age from current roster ownership
  const avgRosterAge = useMemo(() => {
    let totalAge = 0;
    let playerCount = 0;

    Object.keys(rosterOwnership).forEach(playerId => {
      const player = playerData[playerId];

      if (player?.age) {
        totalAge += player.age;
        playerCount++;
      } else if (player?.birth_date) {
        const birthYear = new Date(player.birth_date).getFullYear();
        const currentYear = new Date().getFullYear();
        const age = currentYear - birthYear;
        if (age > 0 && age < 50) {
          totalAge += age;
          playerCount++;
        }
      }
    });

    return playerCount > 0 ? (totalAge / playerCount).toFixed(1) : 'N/A';
  }, [rosterOwnership, playerData]);

  // Calculate record for selected league(s)
  const currentRecord = useMemo(() => {
    if (selectedTeamLeagueId === 'all') {
      // Total record across all leagues
      let totalWins = 0;
      let totalLosses = 0;
      let totalTies = 0;

      mergedLeagues.forEach(league => {
        // Use 2025 record if 2026 is 0-0
        let record = league.record || { wins: 0, losses: 0, ties: 0 };
        if (record.wins === 0 && record.losses === 0 && league.previousSeasonRecord) {
          record = league.previousSeasonRecord;
        }

        if (record) {
          totalWins += record.wins || 0;
          totalLosses += record.losses || 0;
          totalTies += record.ties || 0;
        }
      });

      return { wins: totalWins, losses: totalLosses, ties: totalTies };
    } else {
      // Individual league record - match against originalLeagueIds for merged leagues
      const league = mergedLeagues.find(l =>
        l.id === selectedTeamLeagueId ||
        (l.originalLeagueIds && l.originalLeagueIds.includes(selectedTeamLeagueId))
      );
      if (league && league.record) {
        // Use 2025 record if 2026 is 0-0
        let record = league.record;
        if (record.wins === 0 && record.losses === 0 && league.previousSeasonRecord) {
          record = league.previousSeasonRecord;
        }
        return record;
      }
      return { wins: 0, losses: 0, ties: 0 };
    }
  }, [selectedTeamLeagueId, mergedLeagues]);

  // Filter trades
  const filteredTrades = useMemo(() => {
    let trades = [...allTrades];

    if (selectedTeamLeagueId !== 'all') {
      // For rolled-over leagues, check against originalLeagueIds to include both 2025 and 2026 trades
      const selectedLeague = mergedLeagues.find(l => l.id === selectedTeamLeagueId);
      const leagueIdsToMatch = selectedLeague?.originalLeagueIds || [selectedTeamLeagueId];

      trades = trades.filter(t =>
        leagueIdsToMatch.includes(t.managerIntelLeagueId) ||
        leagueIdsToMatch.includes(t.league?.id)
      );
    }

    if (selectedSeason !== 'all') {
      trades = trades.filter(t => String(t.league.season) === String(selectedSeason));
    }

    if (selectedPosition !== 'all') {
      trades = trades.filter(trade => {
        // Special handling for PICKS filter
        if (selectedPosition === 'PICKS') {
          return trade.draftPicksTraded && trade.draftPicksTraded.length > 0;
        }

        // Regular position filtering
        const players = [...Object.keys(trade.adds), ...Object.keys(trade.drops)];
        return players.some(playerId => playerData[playerId]?.position === selectedPosition);
      });
    }

    if (rosterFilter === 'my_roster' && myRosterPlayerIds.length > 0) {
      trades = trades.filter(trade => {
        const allPlayers = [...Object.keys(trade.adds), ...Object.keys(trade.drops)];
        return allPlayers.some(playerId => myRosterPlayerIds.includes(playerId));
      });
    }

    if (rosterFilter === 'their_roster' && theirRosterPlayerIds.length > 0) {
      trades = trades.filter(trade => {
        const allPlayers = [...Object.keys(trade.adds), ...Object.keys(trade.drops)];
        return allPlayers.some(playerId => theirRosterPlayerIds.includes(playerId));
      });
    }

    // Player search filter
    if (playerSearch.trim()) {
      const searchLower = playerSearch.toLowerCase().trim();
      trades = trades.filter(trade => {
        const allPlayers = [...Object.keys(trade.adds), ...Object.keys(trade.drops)];
        return allPlayers.some(playerId => {
          const player = playerData[playerId];
          if (!player || !player.name) return false;

          // Check player name
          return player.name.toLowerCase().includes(searchLower);
        });
      });
    }

    trades.sort((a, b) => new Date(b.tradedAt) - new Date(a.tradedAt));

    return trades;
  }, [allTrades, selectedTeamLeagueId, selectedSeason, selectedPosition, rosterFilter, playerSearch, myRosterPlayerIds, theirRosterPlayerIds, playerData]);

  // Calculate trade insights - based on FILTERED trades so it updates with league selection
  const insights = useMemo(() => {
    const positionsAcquired = {};
    const positionsTraded = {};
    let picksAccumulated = 0;
    let picksSent = 0;

    filteredTrades.forEach(trade => {
      // Players acquired (adds)
      Object.keys(trade.adds).forEach(playerId => {
        const player = playerData[playerId];
        if (player?.position) {
          positionsAcquired[player.position] = (positionsAcquired[player.position] || 0) + 1;
        }
      });

      // Players traded away (drops)
      Object.keys(trade.drops).forEach(playerId => {
        const player = playerData[playerId];
        if (player?.position) {
          positionsTraded[player.position] = (positionsTraded[player.position] || 0) + 1;
        }
      });

      // Count picks received vs sent
      (trade.draftPicksTraded || []).forEach(pick => {
        if (pick.toRosterId === trade.userRosterId) {
          picksAccumulated++;
        } else if (pick.fromRosterId === trade.userRosterId) {
          picksSent++;
        }
      });
    });

    const mostAcquired = Object.entries(positionsAcquired).sort((a, b) => b[1] - a[1])[0];
    const mostTraded = Object.entries(positionsTraded).sort((a, b) => b[1] - a[1])[0];

    // Calculate net flow for each position
    const positionFlow = {};
    ['QB', 'RB', 'WR', 'TE'].forEach(pos => {
      const acquired = positionsAcquired[pos] || 0;
      const traded = positionsTraded[pos] || 0;
      positionFlow[pos] = {
        acquired,
        traded,
        net: acquired - traded,
      };
    });

    // Add picks flow
    positionFlow.PICKS = {
      acquired: picksAccumulated,
      traded: picksSent,
      net: picksAccumulated - picksSent,
    };

    // Generate trading style insight
    let tradingStyle = '';
    const netPickFlow = picksAccumulated - picksSent;
    const netRBFlow = (positionsAcquired.RB || 0) - (positionsTraded.RB || 0);
    const netWRFlow = (positionsAcquired.WR || 0) - (positionsTraded.WR || 0);

    if (netPickFlow > 5) {
      tradingStyle = 'Pick accumulator. Hoards draft capital for future flexibility. ';
    } else if (netPickFlow < -5) {
      tradingStyle = 'Win-now buyer. Trading picks for proven players. ';
    }

    if (netRBFlow < -3) {
      tradingStyle += 'Selling aging RBs. ';
    }

    if (netWRFlow > 5) {
      tradingStyle += 'Accumulating young WR depth. ';
    }

    if (!tradingStyle) {
      tradingStyle = 'Balanced trading approach. No clear pattern of accumulation or selling.';
    } else {
      tradingStyle += 'Classic trading patterns visible.';
    }

    return {
      mostTradedFor: mostAcquired ? mostAcquired[0] : 'N/A',
      mostTradedAway: mostTraded ? mostTraded[0] : 'N/A',
      picksAccumulated,
      picksSent,
      positionFlow,
      tradingStyle,
    };
  }, [filteredTrades, playerData]);

  // Calculate team concentration across all leagues (or filtered by selected league and position)
  const teamExposureData = useMemo(() => {
    const teamData = {};

    // Use selectedTeamLeagueId for team exposure view (not selectedTeamLeagueId which is for trades)
    const effectiveLeagueId = selectedTeamLeagueId || 'all';

    // Determine which leagues to include
    // When filtering by league, match against originalLeagueIds to handle merged leagues
    const leaguesToInclude = effectiveLeagueId === 'all'
      ? mergedLeagues
      : mergedLeagues.filter(l =>
          l.id === effectiveLeagueId ||
          (l.originalLeagueIds && l.originalLeagueIds.includes(effectiveLeagueId))
        );

    // Track player counts per team across leagues
    const playerTeamLeagues = {}; // { playerId_teamAbbr: count }

    leaguesToInclude.forEach(league => {
      const roster = league.currentRoster || [];
      roster.forEach(playerId => {
        const player = playerData[playerId];
        if (!player) return;

        // Filter by position if selected
        if (selectedPosition !== 'all' && selectedPosition !== 'PICKS' && player.position !== selectedPosition) {
          return;
        }

        const team = player.team || 'FA';
        if (team === 'FA') return; // Skip free agents

        const key = `${playerId}_${team}`;
        playerTeamLeagues[key] = (playerTeamLeagues[key] || 0) + 1;
      });
    });

    // Aggregate players by NFL team
    Object.entries(playerTeamLeagues).forEach(([key, leagueCount]) => {
      const [playerId, team] = key.split('_');
      const player = playerData[playerId];
      if (!player) return;

      if (!teamData[team]) {
        teamData[team] = {
          team,
          totalPlayers: 0,
          leagueCount: leaguesToInclude.length,
          byPosition: { QB: 0, RB: 0, WR: 0, TE: 0 },
          players: [],
        };
      }

      teamData[team].totalPlayers++;
      if (player.position && ['QB', 'RB', 'WR', 'TE'].includes(player.position)) {
        teamData[team].byPosition[player.position]++;
      }
      teamData[team].players.push({
        ...player,
        leagueCount, // How many leagues this player appears in for this team
      });
    });

    // Convert to array and sort by total players
    const result = Object.values(teamData).sort((a, b) => b.totalPlayers - a.totalPlayers);

    // Sort players within each team by KTC value (highest first)
    result.forEach(teamInfo => {
      teamInfo.players.sort((a, b) => {
        const aValue = a.ktc_value_sf || 0;
        const bValue = b.ktc_value_sf || 0;
        return bValue - aValue;
      });
    });

    return result;
  }, [playerData, selectedTeamLeagueId, mergedLeagues, selectedPosition]);


  // Calculate experience distribution across all leagues (or filtered by selected league)
  const experienceData = useMemo(() => {
    const expBuckets = {
      rookies: [], // 0-1 years
      young: [],   // 2-4 years
      prime: [],   // 5-7 years
      aging: [],   // 8+ years
    };

    const byPosition = {
      QB: { rookies: 0, young: 0, prime: 0, aging: 0, total: 0 },
      RB: { rookies: 0, young: 0, prime: 0, aging: 0, total: 0 },
      WR: { rookies: 0, young: 0, prime: 0, aging: 0, total: 0 },
      TE: { rookies: 0, young: 0, prime: 0, aging: 0, total: 0 },
    };

    // Use selectedTeamLeagueId for experience view (not selectedTeamLeagueId which is for trades)
    const effectiveLeagueId = selectedTeamLeagueId || 'all';

    // Determine which leagues to include
    // When filtering by league, match against originalLeagueIds to handle merged leagues
    const leaguesToInclude = effectiveLeagueId === 'all'
      ? mergedLeagues
      : mergedLeagues.filter(l =>
          l.id === effectiveLeagueId ||
          (l.originalLeagueIds && l.originalLeagueIds.includes(effectiveLeagueId))
        );

    // Get players from the selected league(s)
    const playerIds = new Set();
    leaguesToInclude.forEach(league => {
      const roster = league.currentRoster || [];
      roster.forEach(pid => playerIds.add(pid));
    });

    playerIds.forEach(playerId => {
      const player = playerData[playerId];
      if (!player) return;

      // Filter by position if selected
      if (selectedPosition !== 'all' && player.position !== selectedPosition) {
        return;
      }

      const exp = player.yearsExp !== undefined ? player.yearsExp : player.years_exp;
      if (exp === undefined) return;

      let bucket = 'aging';
      if (exp <= 1) bucket = 'rookies';
      else if (exp <= 4) bucket = 'young';
      else if (exp <= 7) bucket = 'prime';

      expBuckets[bucket].push(player);

      // Track by position
      if (player.position && ['QB', 'RB', 'WR', 'TE'].includes(player.position)) {
        byPosition[player.position][bucket]++;
        byPosition[player.position].total++;
      }
    });

    const total = expBuckets.rookies.length + expBuckets.young.length + expBuckets.prime.length + expBuckets.aging.length;

    const result = {
      expBuckets,
      byPosition,
      total,
      percentages: {
        rookies: total > 0 ? Math.round((expBuckets.rookies.length / total) * 100) : 0,
        young: total > 0 ? Math.round((expBuckets.young.length / total) * 100) : 0,
        prime: total > 0 ? Math.round((expBuckets.prime.length / total) * 100) : 0,
        aging: total > 0 ? Math.round((expBuckets.aging.length / total) * 100) : 0,
      },
    };

    return result;
  }, [playerData, selectedTeamLeagueId, selectedPosition, mergedLeagues]);

  if (loading) {
    let cachedAvatar = null;
    try {
      cachedAvatar = localStorage.getItem(avatarCacheKey(sleeperUsername));
    } catch (e) { /* storage unavailable */ }
    return (
      <div className="ft-view">
        <style>{MI_CSS}</style>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '120px 20px 140px' }}>
          <DossierPulse avatarId={cachedAvatar} name={sleeperUsername} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ft-ink)' }}>@{sleeperUsername}</div>
            <LoadingNarration />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-view">
        <span className="ft-k brand">Manager Intel</span>
        <h1 className="ft-hero">
          {error === 'You can only view managers in your leagues' ? 'Access restricted.' : 'Manager not found.'}
        </h1>
        <p className="ft-sub">{error}</p>
        <button className="ft-btn" style={{ marginTop: 22 }} onClick={() => { setError(null); loadProfile(); }}>
          Retry sync
        </button>
      </div>
    );
  }

  if (!manager) return null;

  const isSyncing = syncStatus?.status === 'in_progress';
  const showPortfolioTab =
    (dynastyYoY && (dynastyYoY.length > 0 || dynastyYoY.error === 'TOO_MANY_LEAGUES')) ||
    dynastyYoYStatus === 'computing';

  const selectedLeague = selectedTeamLeagueId !== 'all'
    ? mergedLeagues.find((l) => l.id === selectedTeamLeagueId)
    : null;

  const allRecord = (() => {
    let w = 0;
    let l = 0;
    let t = 0;
    mergedLeagues.forEach((league) => {
      let record = league.record || { wins: 0, losses: 0, ties: 0 };
      if (record.wins === 0 && record.losses === 0 && league.previousSeasonRecord) {
        record = league.previousSeasonRecord;
      }
      w += record.wins || 0;
      l += record.losses || 0;
      t += record.ties || 0;
    });
    return t > 0 ? `${w}-${l}-${t}` : `${w}-${l}`;
  })();

  const filtersActive =
    (selectedTeamLeagueId && selectedTeamLeagueId !== 'all') ||
    selectedSeason !== 'all' ||
    selectedPosition !== 'all' ||
    rosterFilter !== 'all' ||
    !!playerSearch;

  const clearFilters = () => {
    setSelectedTeamLeagueId('all');
    setSelectedSeason('all');
    setSelectedPosition('all');
    setRosterFilter('all');
    setPlayerSearch('');
  };

  const railActive = { background: 'var(--ft-surface)', color: 'var(--ft-ink)', fontWeight: 600 };
  const colHead = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ft-ink-3)', marginBottom: 8 };
  const slotLabel = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ft-ink-3)', opacity: 0.75, margin: '6px 0 1px' };
  const recordMeta = { flex: 'none', fontSize: 12, color: 'var(--ft-ink-3)', fontVariantNumeric: 'tabular-nums' };

  // ---- trades ----

  const splitTradeAssets = (trade) => {
    const gave = [];
    const got = [];
    Object.keys(trade.adds || {}).forEach((playerId) => {
      const player = playerData[playerId];
      if (player) got.push({ type: 'player', data: player });
    });
    Object.keys(trade.drops || {}).forEach((playerId) => {
      const player = playerData[playerId];
      if (player) gave.push({ type: 'player', data: player });
    });
    (trade.draftPicksTraded || []).forEach((pick) => {
      if (pick.toRosterId === trade.userRosterId) got.push({ type: 'pick', data: pick });
      else if (pick.fromRosterId === trade.userRosterId) gave.push({ type: 'pick', data: pick });
    });
    return { gave, got };
  };

  const assetLine = (asset, idx) => (
    <div key={idx} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2.5px 0' }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ft-ink)' }}>
        {asset.type === 'player' ? asset.data.name : `${asset.data.season} Round ${asset.data.round}`}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--ft-ink-3)', flex: 'none' }}>
        {asset.type === 'player'
          ? `${asset.data.position || ''}${asset.data.team ? ` · ${asset.data.team}` : ''}`
          : 'PICK'}
      </span>
    </div>
  );

  const tradeSide = (label, assets) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ft-ink-3)', marginBottom: 3 }}>
        {label}
      </div>
      {assets.length === 0 ? (
        <div style={{ fontSize: 14, color: 'var(--ft-ink-3)', padding: '2.5px 0' }}>Nothing</div>
      ) : (
        assets.map(assetLine)
      )}
    </div>
  );

  const renderTrades = () => (
    <>
      <SectionHead k="Trade ledger" sub="Every trade across synced leagues — what went out, what came back." />

      {filteredTrades.length > 0 && (
        <div className="ft-mi-flowgrid">
          {['QB', 'RB', 'WR', 'TE', 'PICKS'].map((pos) => {
            const flow = insights.positionFlow[pos];
            if (!flow || (flow.acquired === 0 && flow.traded === 0)) return null;
            return (
              <div key={pos} style={{ background: 'var(--ft-surface)', borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{pos === 'PICKS' ? 'Picks' : pos}</span>
                  <span
                    style={{
                      fontSize: 12, fontWeight: 650, padding: '3px 10px', borderRadius: 980, fontVariantNumeric: 'tabular-nums',
                      background: flow.net > 0 ? 'var(--ft-green-soft)' : flow.net < 0 ? 'rgba(255,59,48,.1)' : 'var(--ft-surface-hover, #EBEBED)',
                      color: flow.net > 0 ? 'var(--ft-green-ink)' : flow.net < 0 ? '#C42B22' : 'var(--ft-ink-2)',
                    }}
                  >
                    {flow.net > 0 ? '+' : ''}{flow.net} net
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', marginTop: 6 }}>
                  {flow.acquired} acquired · {flow.traded} traded away
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!(filteredTrades.length === 0 && selectedTeamLeagueId !== 'all' && leagueTradeCountsMap[selectedTeamLeagueId] === 0) && (
        <p className="ft-sub" style={{ fontSize: 13, margin: '0 0 4px' }}>
          {filteredTrades.length} {filteredTrades.length === 1 ? 'trade' : 'trades'}
          {currentRecord.wins > 0 || currentRecord.losses > 0
            ? ` · ${currentRecord.wins}-${currentRecord.losses}${currentRecord.ties > 0 ? `-${currentRecord.ties}` : ''} record`
            : ''}
          {selectedLeague
            ? ` · ${selectedLeague.name}${formatLeagueSettings(selectedLeague) ? ` · ${formatLeagueSettings(selectedLeague)}` : ''}`
            : ''}
        </p>
      )}

      {filteredTrades.length === 0 ? (
        <div style={{ padding: '36px 0' }}>
          {allTrades.length === 0 ? (
            <p className="ft-sub">This manager hasn't made any trades in synced leagues.</p>
          ) : selectedTeamLeagueId !== 'all' && leagueTradeCountsMap[selectedTeamLeagueId] === 0 ? (
            <>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No trades in this league.</div>
              <p className="ft-sub">{manager.displayName || manager.username} hasn't made any trades in this league yet.</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No trades found.</div>
              <p className="ft-sub">Try adjusting your filters to see more results.</p>
            </>
          )}
        </div>
      ) : (
        <div style={{ borderTop: '1px solid var(--ft-hairline)' }}>
          {filteredTrades.map((trade, index) => {
            const leagueName = trade.league?.name || 'Unknown League';
            const tradeDate = trade.tradedAt
              ? new Date(trade.tradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : `Week ${trade.week || 'Unknown'}`;
            const { gave, got } = splitTradeAssets(trade);
            return (
              <div key={trade.id || index} className="ft-mi-trade">
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <SleeperAvatar url={trade.league?.avatarUrl} name={leagueName} size={24} radius="7px" />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {leagueName}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ft-ink-3)' }}>{tradeDate}</div>
                  </div>
                </div>
                {tradeSide('Got', got)}
                <div className="arrow">⇄</div>
                {tradeSide('Gave', gave)}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ---- rosters ----

  const holdingRow = (playerId, player, shares, total) => {
    const pct = Math.round((shares / total) * 100);
    return (
      <div key={playerId} className="ft-mi-holdrow">
        <PlayerHeadshot playerId={playerId} size={34} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{player?.name || 'Unknown Player'}</div>
          <div style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>
            {player?.team || 'FA'} · {player?.position} · Age {player?.age || '—'}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--ft-ink-3)', flex: 'none' }}>{shares} of {total}</span>
        <span className={`ft-chip${pct >= 50 ? ' brand' : ' outline'}`}>{pct}%</span>
      </div>
    );
  };

  const renderPlayerSearch = () => {
    const searchLower = playerSearch.toLowerCase().trim();
    const matchingPlayers = Object.keys(rosterOwnership)
      .map((playerId) => ({ playerId, player: playerData[playerId], leagues: rosterOwnership[playerId] }))
      .filter(({ player }) => player && player.name?.toLowerCase().includes(searchLower));

    if (matchingPlayers.length === 0) {
      return <p className="ft-sub" style={{ padding: '28px 0' }}>No players found matching "{playerSearch}"</p>;
    }

    return matchingPlayers.map(({ playerId, player, leagues: playerLeagues }) => {
      const filteredLeagues = selectedTeamLeagueId === 'all'
        ? playerLeagues
        : playerLeagues.filter((l) => l.leagueId === selectedTeamLeagueId);

      if (filteredLeagues.length === 0 && selectedTeamLeagueId !== 'all') {
        const inLeague = mergedLeagues.find((l) => l.id === selectedTeamLeagueId);
        return (
          <div key={playerId} style={{ padding: '16px 0 28px' }}>
            <div style={{ fontSize: 15.5, fontWeight: 600, marginBottom: 4 }}>
              This manager owns {player.name} but not in {inLeague?.name || 'this league'}
            </div>
            <p className="ft-sub" style={{ marginBottom: 16 }}>
              Select "All leagues" to search across every league this manager plays in.
            </p>
            <span className="ft-k">{player.name} is owned in</span>
            <div className="ft-pillrow" style={{ marginTop: 10 }}>
              {playerLeagues.map((l) => (
                <span key={l.leagueId} className="ft-chip outline">{l.leagueName}</span>
              ))}
            </div>
          </div>
        );
      }
      if (filteredLeagues.length === 0) return null;

      const denominator = selectedTeamLeagueId === 'all' ? mergedLeagues.length : 1;
      return (
        <div key={playerId} style={{ padding: '4px 0 24px' }}>
          {holdingRow(playerId, player, filteredLeagues.length, denominator)}
          <p className="ft-sub" style={{ fontSize: 13, margin: '10px 0' }}>
            {filteredLeagues.length} {filteredLeagues.length === 1 ? 'share' : 'shares'} across {denominator} {denominator === 1 ? 'league' : 'leagues'}
          </p>
          <div className="ft-pillrow">
            {filteredLeagues.map((l) => (
              <span key={l.leagueId} className="ft-chip outline">{l.leagueName}</span>
            ))}
          </div>
        </div>
      );
    });
  };

  const renderLeagueRosterCard = (league) => {
    const roster = league.currentRoster || [];
    const startersArray = league.starters || [];
    const rosterPositions = league.settings?.rosterPositions || [];

    let allPlayers = roster
      .map((playerId) => playerData[playerId])
      .filter((p) => p && ['QB', 'RB', 'WR', 'TE'].includes(p.position));
    if (selectedPosition !== 'all') {
      allPlayers = allPlayers.filter((p) => p.position === selectedPosition);
    }

    let startersWithSlots = startersArray
      .map((playerId, index) => {
        const player = playerData[playerId];
        const rosterSlot = rosterPositions[index] || null;
        return player ? { ...player, rosterSlot } : null;
      })
      .filter(Boolean);
    if (selectedPosition !== 'all') {
      startersWithSlots = startersWithSlots.filter((p) => p.position === selectedPosition);
    }

    const starterIds = new Set(startersArray);
    const benchPlayers = allPlayers.filter((p) => !starterIds.has(p.sleeperId));

    const startersBySlot = {};
    startersWithSlots.forEach((player) => {
      const slot = player.rosterSlot || player.position;
      if (!startersBySlot[slot]) startersBySlot[slot] = [];
      startersBySlot[slot].push(player);
    });

    const benchByPosition = { QB: [], RB: [], WR: [], TE: [] };
    benchPlayers.forEach((player) => {
      if (benchByPosition[player.position]) benchByPosition[player.position].push(player);
    });

    if (allPlayers.length === 0) return null;

    const rawRecord = league.record || { wins: 0, losses: 0, ties: 0 };
    const displayRecord =
      league.season === '2026' && rawRecord.wins === 0 && rawRecord.losses === 0 && league.previousSeasonRecord
        ? league.previousSeasonRecord
        : rawRecord;

    const slotOrder = rosterPositions.length > 0
      ? [...new Set(rosterPositions)]
      : ['QB', 'RB', 'WR', 'TE', 'FLEX', 'SUPER_FLEX'];

    const picksByYear = {};
    (league.draftPicks || []).forEach((pick) => {
      if (!picksByYear[pick.season]) picksByYear[pick.season] = [];
      picksByYear[pick.season].push(pick);
    });

    const playerRow = (player, showPosition) => (
      <div key={player.sleeperId} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0' }}>
        <PlayerHeadshot playerId={player.sleeperId} size={26} />
        <span style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {player.name || 'Unknown Player'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--ft-ink-3)', flex: 'none' }}>
          {showPosition ? `${player.position} · ` : ''}{player.team || 'FA'}
        </span>
      </div>
    );

    return (
      <div key={league.id} className="ft-card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
          <SleeperAvatar url={league.avatarUrl} name={league.name} size={34} radius="9px" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 650 }}>{league.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>
              {league.season} · {allPlayers.length} players · {displayRecord.wins || 0}-{displayRecord.losses || 0}{displayRecord.ties ? `-${displayRecord.ties}` : ''}
              {formatLeagueSettings(league) ? ` · ${formatLeagueSettings(league)}` : ''}
            </div>
          </div>
        </div>
        <div className="ft-mi-rostergrid">
          <div>
            <div style={colHead}>Starting lineup · {startersWithSlots.length}</div>
            {slotOrder.map((slot) => {
              const players = startersBySlot[slot] || [];
              if (players.length === 0) return null;
              return (
                <div key={slot} style={{ marginBottom: 8 }}>
                  <div style={slotLabel}>{String(slot).replace('_', ' ')}</div>
                  {players.map((p) => playerRow(p, slot !== p.position))}
                </div>
              );
            })}
          </div>
          <div>
            <div style={colHead}>Bench · {benchPlayers.length}</div>
            {['QB', 'RB', 'WR', 'TE'].map((pos) => {
              const players = benchByPosition[pos] || [];
              if (players.length === 0) return null;
              return (
                <div key={pos} style={{ marginBottom: 8 }}>
                  <div style={slotLabel}>{pos}</div>
                  {players.map((p) => playerRow(p, false))}
                </div>
              );
            })}
          </div>
        </div>
        {(league.draftPicks || []).length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--ft-hairline)' }}>
            <div style={colHead}>Draft capital · {league.draftPicks.length}</div>
            {Object.entries(picksByYear)
              .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
              .map(([year, picks]) => (
                <div key={year} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '4px 0' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ft-ink-2)', flex: 'none' }}>{year}</span>
                  <div className="ft-pillrow" style={{ gap: 6 }}>
                    {[...picks].sort((a, b) => a.round - b.round).map((pick) => (
                      <span key={pick.id} className="ft-chip outline">{pick.displayName}</span>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const renderRosters = () => {
    if (playerSearch.trim()) return renderPlayerSearch();

    const holdingGroups = ['QB', 'RB', 'WR', 'TE']
      .filter((pos) => selectedPosition === 'all' || selectedPosition === pos)
      .map((position) => ({
        position,
        players: (rosterByPosition[position] || []).filter((p) => p.leagues.length >= 2),
      }))
      .filter((g) => g.players.length > 0);

    return (
      <>
        {selectedTeamLeagueId === 'all' && (
          <>
            <SectionHead k="Core holdings" sub="Players held in two or more leagues — the conviction bets." />
            {holdingGroups.length === 0 ? (
              <p className="ft-sub" style={{ marginBottom: 26 }}>No players held across multiple leagues.</p>
            ) : (
              holdingGroups.map(({ position, players }) => (
                <div key={position} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ft-ink-3)' }}>
                    {POSITION_LABELS[position]} · {players.length}
                  </div>
                  {players.map(({ playerId, player, leagues: playerLeagues }) =>
                    holdingRow(playerId, player, playerLeagues.length, mergedLeagues.length)
                  )}
                </div>
              ))
            )}
            <div style={{ height: 10 }} />
          </>
        )}

        <SectionHead
          k={selectedTeamLeagueId === 'all' ? 'Rosters by league' : 'Roster'}
          sub="Starting lineups, bench depth, and draft capital."
        />
        {mergedLeagues.length === 0 ? (
          <p className="ft-sub">No rosters available.</p>
        ) : (
          mergedLeagues
            .filter((league) => selectedTeamLeagueId === 'all' || league.id === selectedTeamLeagueId)
            .map(renderLeagueRosterCard)
        )}
      </>
    );
  };

  // ---- NFL exposure ----

  const rankedExposure = teamExposureData.slice(0, 12).map((teamInfo) => {
    const totalValue = teamInfo.players.reduce((sum, p) => sum + (p.ktc_value_sf || 0), 0);
    const starterCount = teamInfo.players.filter((p) => p.ktc_value_sf && p.ktc_value_sf > 3000).length;
    const conviction =
      starterCount >= 3 || totalValue >= 4000 ? 'high' : starterCount >= 1 || totalValue >= 2000 ? 'medium' : 'low';
    return { ...teamInfo, totalValue, starterCount, conviction };
  });
  const maxExposureValue = Math.max(...rankedExposure.map((t) => t.totalValue), 1);

  const renderExposure = () => (
    <>
      <SectionHead
        k="NFL exposure"
        sub="A heavy stack reads two ways. They're bullish on that team — sell them those pieces in your league. Or they're overweight and due to unwind."
      />
      {rankedExposure.length === 0 ? (
        <p className="ft-sub">No team exposure data available.</p>
      ) : (
        <>
          <div style={{ marginBottom: 32 }}>
            {rankedExposure.map((t) => (
              <button
                key={t.team}
                className="ft-mi-teambar"
                onClick={() => {
                  setSelectedNFLTeam(t.team);
                  const element = document.getElementById(`team-card-${t.team}`);
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700 }}>{t.team}</span>
                <div style={{ height: 20, borderRadius: 6, background: 'var(--ft-surface)', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.max(3, (t.totalValue / maxExposureValue) * 100)}%`,
                      height: '100%',
                      borderRadius: 6,
                      background: t.conviction === 'high' ? 'var(--ft-brand)' : t.conviction === 'medium' ? '#C7C7CE' : '#E4E4E9',
                      transition: 'width .5s cubic-bezier(.16,1,.3,1)',
                    }}
                  />
                </div>
                <span className="meta" style={{ fontSize: 12, color: 'var(--ft-ink-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {t.totalPlayers} {t.totalPlayers === 1 ? 'player' : 'players'}
                  {t.starterCount ? ` · ${t.starterCount} startable` : ''}
                </span>
              </button>
            ))}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginTop: 14 }}>
              {[
                ['var(--ft-brand)', 'Strong angle'],
                ['#C7C7CE', 'Potential angle'],
                ['#E4E4E9', 'Depth only'],
              ].map(([color, label]) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--ft-ink-3)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: color }} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <SectionHead k="The stacks" sub="Player-level breakdown by NFL team." />
          <div style={{ display: 'grid', gap: 12 }}>
            {rankedExposure.map((t) => (
              <div
                key={t.team}
                id={`team-card-${t.team}`}
                className="ft-card"
                style={selectedNFLTeam === t.team ? { boxShadow: '0 0 0 2px var(--ft-brand)' } : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 750 }}>{t.team}</span>
                  <span className={`ft-chip${t.conviction === 'high' ? ' brand' : ' outline'}`}>
                    {t.conviction === 'high' ? 'Strong angle' : t.conviction === 'medium' ? 'Potential angle' : 'Depth only'}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--ft-ink-3)', fontVariantNumeric: 'tabular-nums' }}>
                    {t.totalPlayers} {t.totalPlayers === 1 ? 'player' : 'players'}
                    {t.starterCount ? ` · ${t.starterCount} startable` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {t.players.map((player) => (
                    <span
                      key={player.sleeperId}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, background: '#FFFFFF', borderRadius: 980,
                        padding: '5px 12px 5px 6px', fontSize: 12.5, fontWeight: 600,
                        boxShadow: player.ktc_value_sf > 3000 ? '0 0 0 1.5px var(--ft-brand)' : 'none',
                      }}
                    >
                      <PlayerHeadshot playerId={player.sleeperId} size={22} />
                      {player.name}
                      <span style={{ color: 'var(--ft-ink-3)', fontWeight: 700, fontSize: 10.5 }}>{player.position}</span>
                      {player.leagueCount > 1 && (
                        <span style={{ fontWeight: 800, fontSize: 11, color: 'var(--ft-brand-ink)' }}>×{player.leagueCount}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );

  // ---- experience ----

  const renderExperience = () => (
    <>
      <SectionHead k="Competitive window" sub="Roster tenure reveals the build — light is young, dark is aging." />
      {experienceData.total === 0 ? (
        <p className="ft-sub">No experience data available.</p>
      ) : (
        <>
          <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', height: 52 }}>
            {EXP_RAMP.map(({ key, bg, fg }) => {
              const pct = experienceData.percentages[key];
              if (!pct) return null;
              return (
                <div
                  key={key}
                  style={{ width: `${pct}%`, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 650 }}
                >
                  {pct >= 8 ? `${pct}%` : ''}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', margin: '12px 0 32px' }}>
            {EXP_RAMP.map(({ key, label, bg }) => (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ft-ink-2)' }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: bg }} />
                {label} · <b style={{ fontWeight: 650 }}>{experienceData.expBuckets[key].length}</b>
              </span>
            ))}
          </div>
          <span className="ft-k">By position</span>
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {['QB', 'RB', 'WR', 'TE'].map((pos) => {
              const d = experienceData.byPosition[pos];
              if (!d.total) return null;
              return (
                <div key={pos} style={{ display: 'grid', gridTemplateColumns: '34px 1fr 40px', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ft-ink-2)' }}>{pos}</span>
                  <div style={{ display: 'flex', height: 22, borderRadius: 7, overflow: 'hidden', background: 'var(--ft-surface)' }}>
                    {EXP_RAMP.map(({ key, bg }) =>
                      d[key] > 0 ? <div key={key} style={{ width: `${(d[key] / d.total) * 100}%`, background: bg }} /> : null
                    )}
                  </div>
                  <span style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {d.total}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );

  // ---- sync state (unified for first-sync and re-sync) ----

  const renderSyncing = () => {
    const total = syncStatus?.leaguesTotal || 0;
    const processed = syncStatus?.leaguesProcessed || 0;
    const isRefresh = allTrades.length > 0;
    // For re-syncs, we already have league data so step 0 is trivially done —
    // jump straight to step 1. For first syncs, wait for leaguesTotal > 0.
    const displayTotal = total || (isRefresh ? leagues.length : 0);
    const activeStep = isRefresh
      ? (processed >= displayTotal && displayTotal > 0 ? 2 : 1)
      : (displayTotal > 0 ? (processed >= displayTotal ? 2 : 1) : 0);
    const steps = ['Pulling leagues', 'Reading leagues', 'Crunching tendencies'];
    return (
      <div style={{ textAlign: 'center', padding: '64px 20px 100px' }}>
        <div style={{ marginBottom: 22 }}>
          <DossierPulse avatarId={manager.avatarUrl} name={manager.displayName || manager.username} size={56} />
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--ft-ink, #1D1D1F)', margin: '0 0 8px' }}>
          {isRefresh ? 'Syncing league history.' : 'Building the intel.'}
        </h2>
        <LoadingNarration />
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 18, textAlign: 'left', marginTop: 32 }}>
          {steps.map((label, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    width: 8, height: 8, borderRadius: '50%', flex: 'none', boxSizing: 'border-box',
                    background: done || active ? 'var(--ft-dark, #000000)' : 'transparent',
                    border: done || active ? 'none' : '1.5px solid var(--ft-hairline, #D2D2D7)',
                    animation: active ? 'ft-syncpulse 1.2s ease-in-out infinite' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
                <span style={{ fontSize: 15, fontWeight: active ? 500 : 400, color: done || active ? 'var(--ft-ink, #1D1D1F)' : 'var(--ft-ink-3, #86868B)' }}>
                  {label}
                </span>
                {active && i === 1 && displayTotal > 0 ? (
                  <span style={{ fontSize: 13, color: 'var(--ft-ink-3, #86868B)' }}>
                    {Math.min(processed, displayTotal)} of {displayTotal}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        {displayTotal > 10 ? (
          <SyncWaitPanel leaguesTotal={displayTotal} processed={processed} username={manager?.username || sleeperUsername} startedAt={syncStartRef.current} />
        ) : null}
      </div>
    );
  };

  // ---- content ladder (same precedence as the legacy surface) ----

  let content = null;
  if (isSyncing) {
    content = renderSyncing();
  } else if (leagues.length === 0) {
    content = null; // "Dynasty leagues only." renders above the tabs
  } else if (activeView === 'dynastyYoY') {
    content = (
      <DynastyYoYSection
        dynastyYoY={dynastyYoY}
        leagues={leagues}
        selectedLeagueId={selectedTeamLeagueId}
        mergedLeagues={mergedLeagues}
        sleeperUsername={sleeperUsername}
        dynastyYoYStatus={dynastyYoYStatus}
        onDynastyYoYUpdate={handleDynastyYoYUpdate}
      />
    );
  } else if (activeView === 'rosters') {
    content = renderRosters();
  } else if (activeView === 'teamExposure') {
    content = renderExposure();
  } else if (activeView === 'experience') {
    content = renderExperience();
  } else {
    content = renderTrades();
  }

  return (
    <div className="ft-view">
      <Helmet prioritizeSeoTags={true} defer={false}>
        <title>{manager ? `${sleeperUsername} - Dynasty Manager Intel | Fantasy Taco` : 'Manager Intel | Fantasy Taco'}</title>
        <meta
          name="description"
          content={
            manager && trophyCase
              ? `${sleeperUsername} has ${trophyCase.totalTrades || 0} dynasty trades across ${trophyCase.leagueCount || 0} leagues with a ${trophyCase.winPercentage || 0}% win rate. View complete trade history and championship record.`
              : `View ${sleeperUsername}'s complete dynasty fantasy football trade history, win rate, and championship record on Sleeper.`
          }
        />
        <link rel="canonical" href={`https://fantasytaco.com/manager-intel/${sleeperUsername}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={`${sleeperUsername} - Dynasty Manager Intel`} />
        <meta
          property="og:description"
          content={
            manager && trophyCase
              ? `${trophyCase.totalTrades || 0} trades · ${trophyCase.leagueCount || 0} leagues · ${trophyCase.winPercentage || 0}% win rate · ${trophyCase.championships || 0} championships`
              : `View complete dynasty trade history and championship record`
          }
        />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://fantasytaco.com/manager-intel/${sleeperUsername}`} />
        {manager?.avatar && (
          <meta property="og:image" content={`https://sleepercdn.com/avatars/thumbs/${manager.avatar}`} />
        )}
        <meta property="og:site_name" content="Fantasy Taco" />
        <meta property="profile:username" content={sleeperUsername} />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@fantasytaco420" />
        <meta name="twitter:title" content={`${sleeperUsername} - Dynasty Manager Intel`} />
        <meta
          name="twitter:description"
          content={
            manager && trophyCase
              ? `${trophyCase.totalTrades || 0} trades · ${trophyCase.leagueCount || 0} leagues · ${trophyCase.winPercentage || 0}% win rate`
              : `View complete dynasty trade history`
          }
        />
        {manager?.avatar && (
          <meta name="twitter:image" content={`https://sleepercdn.com/avatars/thumbs/${manager.avatar}`} />
        )}

        {/* Structured Data - Person Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: sleeperUsername,
            url: `https://fantasytaco.com/manager-intel/${sleeperUsername}`,
            image: manager?.avatar ? `https://sleepercdn.com/avatars/thumbs/${manager.avatar}` : undefined,
            description: manager && trophyCase
              ? `Dynasty fantasy football manager with ${trophyCase.totalTrades || 0} trades across ${trophyCase.leagueCount || 0} leagues`
              : `Dynasty fantasy football manager on Sleeper`,
            sameAs: `https://sleeper.com/leagues/nfl`,
          })}
        </script>
      </Helmet>
      <style>{MI_CSS}</style>

      <ShellRail>
        <div className="ft-railsec" style={{ marginTop: 0 }}>
          <button className="ft-btn sm" style={{ width: '100%' }} onClick={handleRefresh} disabled={isSyncing}>
            {isSyncing ? 'Syncing…' : syncStatus?.status === 'stale' ? 'Sync interrupted — retry' : 'Refresh intel'}
          </button>
          <p className="ft-sub" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
            {syncStatus?.status === 'stale' ? 'Sync was interrupted. Hit retry to rebuild.' : `Updated ${formatTimestamp(lastRefreshedAt)}`}
          </p>
        </div>
        {dossierData && <DossierRail data={dossierData} />}

        {/* Upsell — pinned to the very bottom of the rail (.ft-railpanel is a flex
            column; margin-top:auto pushes this down). Prompts the viewer to get
            a trade professionally reviewed. */}
        <div className="ft-railsec" style={{ marginTop: 'auto', marginBottom: 0 }}>
          <div style={{ borderRadius: 14, padding: 16, background: 'linear-gradient(160deg, #FFF3E9, #FFE7D3)', boxShadow: 'inset 0 0 0 1px #FFD9BD' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#C85E00', marginBottom: 6 }}>Analyst Review</div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1D1D1F', lineHeight: 1.4, marginBottom: 12 }}>A real dynasty analyst breaks down your next trade before you make it.</div>
            <button className="ft-btn sm" style={{ width: '100%' }} onClick={() => { window.open('/analyst/fantasynerdboi', '_blank', 'noopener'); }}>Get a trade reviewed</button>
          </div>
        </div>

        {/* Rail leagues list — legacy nav for the old activeView filtering; the dossier
            has its own per-tab league filters, so this is redundant. Hidden for reversibility. */}
        {false && mergedLeagues.length > 0 && (
          <div className="ft-railsec">
            <span className="ft-k">Leagues</span>
            <button
              className="ft-railaction"
              style={selectedTeamLeagueId === 'all' ? railActive : undefined}
              onClick={() => setSelectedTeamLeagueId('all')}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>All leagues</span>
              <span style={recordMeta}>{allRecord}</span>
            </button>
            {mergedLeagues.map((league) => {
              let displayRecord = league.record || { wins: 0, losses: 0, ties: 0 };
              if (displayRecord.wins === 0 && displayRecord.losses === 0 && league.previousSeasonRecord) {
                displayRecord = league.previousSeasonRecord;
              }
              const hasRecord = displayRecord.wins > 0 || displayRecord.losses > 0 || displayRecord.ties > 0;
              const recordText = hasRecord
                ? displayRecord.ties > 0
                  ? `${displayRecord.wins}-${displayRecord.losses}-${displayRecord.ties}`
                  : `${displayRecord.wins}-${displayRecord.losses}`
                : '';
              return (
                <button
                  key={league.id}
                  className="ft-railaction"
                  style={selectedTeamLeagueId === league.id ? railActive : undefined}
                  onClick={() => setSelectedTeamLeagueId(league.id)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <SleeperAvatar url={league.avatarUrl} name={league.name} size={20} radius="6px" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{league.name}</span>
                  </span>
                  {recordText ? <span style={recordMeta}>{recordText}</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {/* Old activeView filter controls — superseded by the DossierSection Brief. Hidden, kept for reversibility. */}
        {false && !isSyncing && leagues.length > 0 && activeView !== 'dynastyYoY' && (
          <div className="ft-railsec">
            <span className="ft-k">Filters</span>
            {(activeView === 'trades' || activeView === 'rosters') && (
              <input
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                placeholder="Search player"
                style={{
                  width: '100%', background: 'var(--ft-surface)', border: 'none', borderRadius: 10,
                  padding: '10px 12px', fontSize: 13.5, outline: 'none', marginTop: 4,
                }}
              />
            )}
            {activeView === 'trades' && seasons.length > 1 && (
              <div className="ft-pillrow" style={{ marginTop: 10 }}>
                {['all', ...seasons.map(String)].map((season) => (
                  <button
                    key={season}
                    className={`ft-fpill${String(selectedSeason) === season ? ' on' : ''}`}
                    onClick={() => setSelectedSeason(season)}
                  >
                    {season === 'all' ? 'All seasons' : season}
                  </button>
                ))}
              </div>
            )}
            {positions.length > 0 && (
              <div className="ft-pillrow" style={{ marginTop: 10 }}>
                {['all', ...positions.filter((pos) => (activeView !== 'rosters' && activeView !== 'teamExposure') || pos !== 'PICKS')].map((pos) => (
                  <button
                    key={pos}
                    className={`ft-fpill${selectedPosition === pos ? ' on' : ''}`}
                    onClick={() => setSelectedPosition(pos)}
                  >
                    {pos === 'all' ? 'All positions' : pos === 'PICKS' ? 'Picks' : pos}
                  </button>
                ))}
              </div>
            )}
            {(myRosterPlayerIds.length > 0 || theirRosterPlayerIds.length > 0) && (
              <div className="ft-pillrow" style={{ marginTop: 10 }}>
                <button className={`ft-fpill${rosterFilter === 'all' ? ' on' : ''}`} onClick={() => setRosterFilter('all')}>
                  All trades
                </button>
                {myRosterPlayerIds.length > 0 && (
                  <button className={`ft-fpill${rosterFilter === 'my_roster' ? ' on' : ''}`} onClick={() => setRosterFilter('my_roster')}>
                    My roster
                  </button>
                )}
                {theirRosterPlayerIds.length > 0 && (
                  <button className={`ft-fpill${rosterFilter === 'their_roster' ? ' on' : ''}`} onClick={() => setRosterFilter('their_roster')}>
                    Their roster
                  </button>
                )}
              </div>
            )}
            {filtersActive && (
              <button className="ft-railaction" style={{ marginTop: 8, color: 'var(--ft-ink-3)' }} onClick={clearFilters}>
                Clear all filters
              </button>
            )}
          </div>
        )}
      </ShellRail>

      <span className="ft-k brand">Manager Intel</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
        <SleeperAvatar id={manager.avatarUrl} name={manager.displayName || manager.username} size={56} />
        <div style={{ minWidth: 0 }}>
          <h1 className="ft-hero" style={{ margin: 0 }}>{manager.displayName || manager.username}</h1>
          <p className="ft-sub" style={{ margin: '4px 0 0' }}>@{manager.username}</p>
        </div>
      </div>

      {!isSyncing && <DossierSection sleeperUsername={sleeperUsername} onData={setDossierData} leagues={leagues} />}

      <div id="mi-history" style={{ scrollMarginTop: 64 }} />

      {/* FIX #25 — legacy trophy-case stat band (DYNASTY ACTIVITY / TRACK RECORD /
          THE READ) hidden: it conflicts with the DossierSection Brief (mismatched
          trade counts) and is awkwardly placed. Page is now header → DossierSection.
          Data/memos above stay defined for reversibility; just not rendered. */}
      {false && !isSyncing && trophyCase && (() => {
        // Two honestly-scoped stat groups: leagues/trades/age span the synced
        // window, while titles, playoff runs, and win rate can only come from
        // completed seasons — mixing them reads like 2026 results exist.
        const flows = ['QB', 'RB', 'WR', 'TE'].map((pos) => ({ pos, net: insights.positionFlow[pos]?.net || 0 }));
        const topBuy = flows.reduce((a, b) => (b.net > a.net ? b : a));
        const topSell = flows.reduce((a, b) => (b.net < a.net ? b : a));
        const pickNet = insights.picksAccumulated - insights.picksSent;

        // Trades per competed league-season: startups added this season are
        // excluded from both sides of the division.
        const currentSeason = leagues.reduce((max, l) => (Number(l.season) > Number(max) ? l.season : max), '0');
        const isCurrentSeasonStartup = (l) => {
          const prev = l.settings?.previousLeagueId;
          return (!prev || prev === '0') && String(l.season) === String(currentSeason);
        };
        const competedLeagueSeasons = leagues.filter((l) => !isCurrentSeasonStartup(l)).length;
        const startupLeagueIds = new Set(leagues.filter(isCurrentSeasonStartup).map((l) => l.id));
        const countedTrades = allTrades.filter(
          (t) => !startupLeagueIds.has(t.managerIntelLeagueId) && !startupLeagueIds.has(t.league?.id)
        ).length;
        const perLeagueSeason = countedTrades / Math.max(competedLeagueSeasons, 1);
        const ratio = perLeagueSeason / 3.5;

        const statSeasons = [...new Set(leagues.map((l) => String(l.season)))].sort();
        const shortYear = (y) => (String(y).length === 4 ? String(y).slice(2) : String(y));
        const windowLabel = statSeasons.length === 0
          ? 'Dynasty activity'
          : statSeasons.length === 1
          ? `Dynasty activity · ${statSeasons[0]}`
          : `Dynasty activity · ${statSeasons[0]}–${shortYear(statSeasons[statSeasons.length - 1])}`;

        const playedRows = leagues.filter((l) => Number(l.season) < Number(currentSeason));
        const playedSeasons = [...new Set(playedRows.map((l) => String(l.season)))].sort();
        const played = playedRows.reduce(
          (acc, l) => ({
            wins: acc.wins + (l.record?.wins || 0),
            losses: acc.losses + (l.record?.losses || 0),
            ties: acc.ties + (l.record?.ties || 0),
          }),
          { wins: 0, losses: 0, ties: 0 }
        );
        const recordStr = `${played.wins}-${played.losses}${played.ties ? `-${played.ties}` : ''}`;

        const chips = [];
        if (topBuy.net >= 2) chips.push(`Net buyer · ${topBuy.pos} +${topBuy.net}`);
        if (topSell.net <= -2) chips.push(`Net seller · ${topSell.pos} ${topSell.net}`);
        chips.push(Math.abs(pickNet) <= 2 ? 'Pick-neutral' : pickNet > 0 ? `Hoards picks +${pickNet}` : `Ships picks ${pickNet}`);
        if (chips.length === 1 && Math.abs(pickNet) <= 2) chips.unshift('Balanced — no strong lean');

        return (
          <>
            <div className="ft-card ft-mi-statgroups">
              <div className="ft-mi-statgroup">
                <span className="ft-k">{windowLabel}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px 36px', marginTop: 12 }}>
                  <Stat
                    value={mergedLeagues.filter((l) => String(l.season) === String(currentSeason)).length}
                    label="Active leagues"
                  />
                  <Stat value={trophyCase.totalTrades} label="Trades" />
                  <Stat
                    value={perLeagueSeason.toFixed(1)}
                    label="Trades / league / yr"
                    sub={ratio >= 1.25 ? `≈${ratio >= 1.75 ? `${Math.round(ratio)}×` : `${ratio.toFixed(1)}×`} typical` : ratio <= 0.6 ? 'Quiet trader' : 'About average'}
                  />
                  <Stat value={avgRosterAge} label="Avg age" />
                </div>
              </div>
              <div className="ft-mi-statgroup">
                <span className="ft-k">{playedSeasons.length ? `Track record · ${playedSeasons.join('–')}` : 'Track record'}</span>
                {playedSeasons.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px 36px', marginTop: 12 }}>
                    <Stat value={playedRows.length} label="Leagues" />
                    <Stat value={trophyCase.championships} label="Titles" />
                    <Stat value={trophyCase.playoffAppearances} label="Playoff runs" />
                    <Stat value={`${trophyCase.winPercent.toFixed(0)}%`} label={`Win rate · ${recordStr}`} />
                  </div>
                ) : (
                  <p className="ft-sub" style={{ fontSize: 13, marginTop: 12, maxWidth: 220 }}>
                    First season — the record starts this year.
                  </p>
                )}
              </div>
            </div>

            {!isSyncing && allTrades.length > 0 && (
              <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span className="ft-k">The read</span>
                <div className="ft-pillrow">
                  {chips.map((text) => (
                    <span key={text} className="ft-chip outline">{text}</span>
                  ))}
                  {selectedLeague ? <span className="ft-chip brand">{selectedLeague.name}</span> : null}
                </div>
              </div>
            )}
          </>
        );
      })()}
      {!isSyncing && leagues.length === 0 && dossierData !== undefined && (
        <div style={{ padding: '56px 0 40px', maxWidth: 480 }}>
          <h2 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 10px' }}>Dynasty leagues only.</h2>
          <p className="ft-sub" style={{ marginBottom: 6 }}>
            Manager Intel currently only supports dynasty leagues. This manager doesn't have any dynasty leagues on Sleeper.
          </p>
          <p className="ft-sub" style={{ fontSize: 13 }}>Redraft and keeper leagues will be supported in the future.</p>
        </div>
      )}

      {/* Old activeView tab pills — superseded by the DossierSection Brief tabs. Hidden, kept for reversibility. */}
      {false && !isSyncing && leagues.length > 0 && (
        <div style={{ margin: '24px 0 26px', overflowX: 'auto' }}>
          <div style={{ display: 'inline-flex', background: 'var(--ft-surface)', borderRadius: 'var(--ft-r-pill, 980px)', padding: 3, whiteSpace: 'nowrap' }}>
            {[
              ...(showPortfolioTab ? [['dynastyYoY', 'Portfolio']] : []),
              ['trades', 'Trades'],
              ['rosters', 'Rosters'],
              ['teamExposure', 'NFL exposure'],
              ['experience', 'Experience'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveView(key)}
                style={{
                  borderRadius: 'var(--ft-r-pill, 980px)', padding: '9px 20px', fontSize: 13.5, fontWeight: 600,
                  background: activeView === key ? 'var(--ft-dark, #1D1D1F)' : 'transparent',
                  color: activeView === key ? '#FFFFFF' : 'var(--ft-ink-3)',
                  transition: 'all .2s', flex: 'none',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/*
        Main column is now the DossierSection Brief (rendered above). Only the
        sync/loading states from the legacy `content` ladder still render here;
        the old activeView views (trades/rosters/exposure/experience/dynastyYoY)
        are hidden — `content` is non-null for those but intentionally not output.
        Kept reversible: remove the `isSyncing &&` guard to restore the old views.
      */}
      {isSyncing && content}

      {showDynastyYoYToast && <div className="ft-toast show">Dynasty portfolio ready</div>}
    </div>
  );
};

export default ManagerIntelProfile;
